// @flow
import { useState, useMemo, useRef } from 'react';
import sortedIndexBy from 'lodash/sortedIndexBy';
import { getPageContext } from 'components/PageContext';
import type { BlokComponentType } from 'components/types';
import type { CmsShopType, TCApiShopType } from './types';
import { ApiShopsProvider } from './apiShopsContext';
import { GeoLocationProvider } from './geoLocationContext';
import Filters from './Filters';
import renderComponent from '../renderComponent';
import componentKeys from '../../../componentKeys';
import ShopCard from './ShopCard';
import { ShopListGrid } from './styled';

type PropsType = {
  blok: {
    areas_label?: string,
    cached_api_shops?: { [string]: TCApiShopType },
    default_shop_reserve_label: string,
    shops: BlokComponentType<CmsShopType>[],
    tags_label?: string
  }
};

function insertOption(
  options: { label: string, value: string }[],
  newOption: string
) {
  if (!newOption) return;
  const option = {
    label: newOption,
    value: newOption.toLowerCase().trim()
  };
  if (!options.length) {
    options.push(option);
    return;
  }
  const insertTagIndex = sortedIndexBy(options, option, ({ value }) => value);
  if (
    !options[insertTagIndex] ||
    options[insertTagIndex].value !== option.value
  ) {
    options.splice(insertTagIndex, 0, option);
  }
}

const ShopList = ({
  blok: {
    shops,
    tags_label: tagsLabel,
    areas_label: areasLabel,
    cached_api_shops: apiShops = {},
    default_shop_reserve_label: defaultReserveLabel
  }
}: PropsType) => {
  const apiShopsRef = useRef<{ [string]: TCApiShopType }>(apiShops);
  const currentLocationRef = useRef();
  const [filteredShops, setFilteredShops] = useState(shops.slice());

  const filterShops = (selectedAreas, selectedTags) => {
    // we need this to make sure we don't accidentally change the original order
    let result = shops.slice();
    if (selectedAreas.length || selectedTags.length) {
      result = shops.filter((shop) => {
        let hasMatchingTag = true;
        if (selectedTags.length) {
          hasMatchingTag = (shop.tags || []).reduce(
            (hasTag, tag) =>
              hasTag ||
              selectedTags.find(
                (selectedTag) => selectedTag === tag.toLowerCase().trim()
              ),
            false
          );
        }
        let hasMatchingArea = true;
        if (selectedAreas.length) {
          hasMatchingArea = selectedAreas.find(
            (selectedArea) => selectedArea === shop.area.toLowerCase().trim()
          );
        }
        return hasMatchingArea && hasMatchingTag;
      });
    }
    setFilteredShops(result);
  };

  const {
    story: { full_slug: storySlug }
  } = getPageContext();
  const path = storySlug.split('/').slice(-2, -1)[0];

  const [tagOptions, areaOptions] = useMemo(
    () =>
      shops.reduce(
        (optionsTuple, shop) => {
          (shop.tags || []).forEach((tag) =>
            insertOption(optionsTuple[0], tag)
          );
          if (shop.area) {
            insertOption(optionsTuple[1], shop.area);
          }
          return optionsTuple;
        },
        [[], []]
      ),
    [shops]
  );
  const shopSlugs = useMemo(() => shops.map(({ slug }) => slug), [shops]);

  return (
    <GeoLocationProvider currentLocationRef={currentLocationRef}>
      <ApiShopsProvider slugs={shopSlugs} apiShopsRef={apiShopsRef}>
        <Filters
          tagOptions={tagOptions}
          areaOptions={areaOptions}
          areasLabel={areasLabel}
          tagsLabel={tagsLabel}
          filterShops={filterShops}
        />
        <ShopListGrid>
          {filteredShops.map((shop) =>
            renderComponent(
              shop,
              {
                customPath: 'components/editable/ShopsList/index.jsx',
                key: shop._uid,
                utmSource: path,
                defaultReserveLabel
              },
              {
                [componentKeys.SHOP]: ShopCard
              }
            )
          )}
        </ShopListGrid>
      </ApiShopsProvider>
    </GeoLocationProvider>
  );
};

export default ShopList;
